import React, { SyntheticEvent, useEffect, useState } from 'react';
import { StyledInputGroup, StyledInputMinus, StyledInputPlus, StyledInputQuantity } from './styled';
type Props = {
    productName: string;
    defaultValue: number;
    handleMinus?: () => void;
    handlePlus?: () => void;
    onChange: (value: number) => void;
    disableMinus?: boolean;
    disablePlus?: boolean;
    readOnly?: boolean;
    maxAvailableQuantity?: number;
    decreaseLabel?: string;
    increaseLabel?: string;
};

export const InputPlusMinus = ({
    productName,
    defaultValue,
    handleMinus,
    handlePlus,
    disableMinus,
    disablePlus,
    readOnly,
    onChange,
    maxAvailableQuantity,
    decreaseLabel = `Fjern 1 ${productName} fra kurv`,
    increaseLabel = `Tilføj 1 ${productName} til kurv`,
}: Props) => {
    const [value, setValue] = useState(defaultValue.toString());
    const handleChange = (e: SyntheticEvent<HTMLInputElement>) => {
        const input = e.currentTarget.value;
        const value = parseInt(input, 10);
        if (!value) {
            setValue('');
        } else {
            if (value && value >= 1) {
                setValue(value.toString());
            } else {
                setValue('1');
            }
        }
    };

    useEffect(() => {
        if (parseInt(value, 10) !== defaultValue) {
            setValue(defaultValue.toString());
        }
    }, [defaultValue]);

    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        const newValue = parseInt(e.target.value, 10);

        // Empty or unusable input falls back to the lowest valid quantity.
        if (Number.isNaN(newValue) || newValue < 1) {
            setValue('1');
            onChange(1);
            return;
        }

        // Only clamp when max quantity is set.
        if (maxAvailableQuantity !== undefined && newValue > maxAvailableQuantity) {
            setValue(maxAvailableQuantity.toString());
            onChange(maxAvailableQuantity);
            return;
        }

        onChange(newValue);
    };

    const shouldDisableMinus = disableMinus || readOnly;
    const shouldDisablePlus = disablePlus || readOnly;

    return (
        <StyledInputGroup>
            <StyledInputMinus
                aria-label={decreaseLabel}
                disabled={shouldDisableMinus}
                onClick={shouldDisableMinus ? undefined : handleMinus}
                tabIndex={shouldDisableMinus ? -1 : 0}
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        !shouldDisableMinus && handleMinus?.();
                    }
                }}
            />
            <StyledInputQuantity
                aria-label="Mængde inputfelt"
                value={value}
                onChange={handleChange}
                onBlur={handleOnBlur}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                    }
                }}
                readOnly={readOnly}
                name="Mængde inputfelt"
            />
            <StyledInputPlus
                aria-label={increaseLabel}
                onClick={shouldDisablePlus ? undefined : handlePlus}
                disabled={shouldDisablePlus}
                tabIndex={shouldDisablePlus ? -1 : 0}
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        shouldDisablePlus ? undefined : handlePlus?.();
                    }
                }}
            />
        </StyledInputGroup>
    );
};
